import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import {
    PlacementTopicsResponse,
    SkillExtractionResponse,
    CreateAptitudeTestResponse,
    GetCompaniesResponse,
    StartAttemptResponse,
    SaveProgressResponse,
    SubmitTestResponse,
    DeleteAttemptResponse,
    GetTestHistoryResponse,
    GetAttemptsResponse,
    TestConversationItem,
    MockTestCreationResponse,
    GetAttemptsByTestIdResponse
} from '../../models/placement/placement.model';
import { StudentTestAttemptResponses } from '@edtech-ui/core/models/placement/get-attempts-by-id';

@Injectable({
    providedIn: 'root'
})
export class PlacementService {
    apiUrl = environment.placementPreparationUrl;

    constructor(private http: HttpClient) { }

    getTopics(category: string): Observable<PlacementTopicsResponse> {
        return this.http.get<PlacementTopicsResponse>(`${this.apiUrl}/getTopics/${category}`);
    }

    extractSkills(file: File): Observable<SkillExtractionResponse> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<SkillExtractionResponse>(`${this.apiUrl}/extractSkills`, formData);
    }

    createResumeBasedTest(
        file: File,
        difficulty: 'EASY' | 'MEDIUM' | 'HARD',
        numQuestions: number
    ): Observable<CreateAptitudeTestResponse> {
        const formData = new FormData();
        formData.append('file', file);
        const params = `?difficulty=${difficulty}&num_questions=${numQuestions}&is_public=false`;
        return this.http.post<CreateAptitudeTestResponse>(`${this.apiUrl}/createResumeBasedTest${params}`, formData);
    }

    createAptitudeTest(
        topics: string[],
        difficulty: string,
        numQuestions: number,
        isPublic: boolean = false,
        testName: string = ''
    ): Observable<MockTestCreationResponse> {
        const body = {
            topics: topics,
            difficulty: difficulty,
            num_questions: numQuestions,
            is_public: isPublic,
            test_name: testName
        };
        return this.http.post<MockTestCreationResponse>(`${this.apiUrl}/createAptitudeTest`, body);
    }

    createLogicalTest(
        topics: string[],
        difficulty: string,
        numQuestions: number,
        isPublic: boolean = false,
        testName: string = ''
    ): Observable<MockTestCreationResponse> {
        const body = {
            topics: topics,
            difficulty: difficulty,
            num_questions: numQuestions,
            is_public: isPublic,
            test_name: testName
        };
        return this.http.post<MockTestCreationResponse>(`${this.apiUrl}/createLogicalTest`, body);
    }

    createVerbalTest(
        topics: string[],
        difficulty: string,
        numQuestions: number,
        isPublic: boolean = false,
        testName: string = ''
    ): Observable<MockTestCreationResponse> {
        const body = {
            topics: topics,
            difficulty: difficulty,
            num_questions: numQuestions,
            is_public: isPublic,
            test_name: testName
        };
        return this.http.post<MockTestCreationResponse>(`${this.apiUrl}/createVerbalTest`, body);
    }

    createProgrammingTest(
        topics: string[],
        difficulty: string,
        numQuestions: number,
        isPublic: boolean = false,
        testName: string = ''
    ): Observable<MockTestCreationResponse> {
        const body = {
            topics: topics,
            difficulty: difficulty,
            num_questions: numQuestions,
            is_public: isPublic,
            test_name: testName
        };
        return this.http.post<MockTestCreationResponse>(`${this.apiUrl}/createProgrammingTest`, body);
    }

    createMixedTest(
        difficulty: string,
        numQuestions: number,
        isPublic: boolean = false,
        testName: string = ''
    ): Observable<MockTestCreationResponse> {
        const body = {
            difficulty: difficulty,
            num_questions: numQuestions,
            is_public: isPublic,
            test_name: testName
        };
        return this.http.post<MockTestCreationResponse>(`${this.apiUrl}/createMixedTest`, body);
    }

    getCompanies(): Observable<GetCompaniesResponse> {
        return this.http.get<GetCompaniesResponse>(`${this.apiUrl}/getCompanies`);
    }

    createCompanySpecificTest(
        companyName: string,
        role: string,
        difficulty: string,
        numQuestions: number,
        isPublic: boolean = false,
        testName: string = ''
    ): Observable<MockTestCreationResponse> {
        const body = {
            company_name: companyName,
            role: role,
            difficulty: difficulty,
            num_questions: numQuestions,
            is_public: isPublic,
            test_name: testName
        };
        return this.http.post<MockTestCreationResponse>(`${this.apiUrl}/createCompanySpecificTest`, body);
    }

    startAttempt(testId: string): Observable<StartAttemptResponse> {
        return this.http.post<StartAttemptResponse>(`${this.apiUrl}/startAttempt`, { test_id: testId });
    }

    saveProgress(attemptId: string, testConversation: TestConversationItem[], timeTaken: number): Observable<SaveProgressResponse> {
        return this.http.patch<SaveProgressResponse>(`${this.apiUrl}/saveProgress`, {
            attempt_id: attemptId,
            test_conversation: testConversation,
            time_taken: timeTaken
        });
    }

    submitTest(attemptId: string, testConversation: TestConversationItem[], timeTaken?: number): Observable<SubmitTestResponse> {
        return this.http.post<SubmitTestResponse>(`${this.apiUrl}/submitTest`, {
            attempt_id: attemptId,
            test_conversation: testConversation,
            time_taken: timeTaken
        });
    }

    deleteAttempt(attemptId: string): Observable<DeleteAttemptResponse> {
        return this.http.delete<DeleteAttemptResponse>(`${this.apiUrl}/deleteAttempt/${attemptId}`);
    }

    getTestHistory(page: number = 1, limit: number = 10, filter: string = 'my_tests', sort: string = 'created_at', order: string = 'desc'): Observable<GetTestHistoryResponse> {
        const params = `?page=${page}&limit=${limit}&filter=${filter}&sort=${sort}&order=${order}`;
        return this.http.get<GetTestHistoryResponse>(`${this.apiUrl}/getTestHistory${params}`);
    }

    getAttempts(page: number = 1, limit: number = 10, test_type: string | null = null, status: string | null = null, sort: string = 'created_at', order: string = 'desc'): Observable<GetAttemptsResponse> {
        let params = `?page=${page}&limit=${limit}&sort=${sort}&order=${order}`;
        if (test_type) {
            params += `&test_type=${test_type}`;
        }
        if (status) {
            params += `&status=${status}`;
        }
        return this.http.get<GetAttemptsResponse>(`${this.apiUrl}/getAttempts${params}`);
    }
    createResumeBasedTestFromData(
        skills: string[],
        experience: string[],
        difficulty: string,
        numQuestions: number,
        isPublic: boolean = false,
        testName: string = ''
    ): Observable<MockTestCreationResponse> {
        const body = {
            skills: skills,
            experience: experience,
            difficulty: difficulty,
            num_questions: numQuestions,
            is_public: isPublic,
            test_name: testName
        };
        return this.http.post<MockTestCreationResponse>(`${this.apiUrl}/createResumeBasedTest`, body);
    }

	getAttemptById(attemptId: string): Observable<StudentTestAttemptResponses> {
		return this.http.get<StudentTestAttemptResponses>(`${this.apiUrl}/getAttemptById/${attemptId}`);
	}

    getAttemptsByTestId(testId: string, page: number = 1, limit: number = 10): Observable<GetAttemptsByTestIdResponse> {
        const params = `?page=${page}&limit=${limit}`;
        return this.http.get<GetAttemptsByTestIdResponse>(`${this.apiUrl}/getAttemptsByTestId/${testId}${params}`);
    }
}
